/* eslint-disable @typescript-eslint/no-explicit-any */
import { create } from "zustand";
import { RemoteDataSource, Settings } from "@/datasources/remote_datasource";

import type {
  Solicitacao,
  SolicitacaoFiltros,
  CategoriaSolicitacao,
  DashboardMetricas,
  StatusSolicitacao,
  SolicitacaoHistorico,
  SolicitacaoAgente,
  SolicitacaoComentario,
  SolicitacaoAnexo,
  ResultadoValidacao,
  TipoValidacao,
} from "@/types/solicitacao";
import type { SolicitacaoParcial } from "@/components/solicitacoes/core/models";
import { AppConfig } from "@/datasources/AppConfig";
import { OUVIDORIA_ENDPOINTS } from "@/config/ouvidoria";

const api = new RemoteDataSource(Settings.urlBase);

// Endpoint base para solicitações da ouvidoria (usado para operações específicas)
const getSolicitacoesEndpoint = () => `v1/municipios/${AppConfig.municipioId}/ouvidorias/${AppConfig.ouvidoriaId}/solicitacoes`;

// Endpoint para buscar solicitações atribuídas a um agente
const SOLICITACOES_AGENTE_ENDPOINT = `solicitacoes`;

const SOLICITACOES_ENDPOINT_DETAILS = `v1/solicitacoes`;

interface Ouvidoria {
  id: string;
  nome: string;
  cnpj?: string;
  logo?: { arquivo?: string } | null;
  nomeFantasia?: string | null;
  etiquetas?: string[] | null;
  // Hierarquia (Response.Ouvidoria.Compacta)
  supervisoraId?: string | null;
  status?: string | null;
  instituicao?: string | null;
}

interface SolicitacoesState {
  solicitacoes: Solicitacao[];
  solicitacoesParciais: SolicitacaoParcial[];
  // Lista completa e não filtrada — usada exclusivamente para os cards de contagem
  statsSolicitacoes: SolicitacaoParcial[];
  solicitacaoAtual: Solicitacao | null;
  categorias: CategoriaSolicitacao[];
  metricas: DashboardMetricas | null;
  totalSolicitacoes: number;
  ouvidoriasSupervisionadas: Ouvidoria[];

  isLoading: boolean;
  isLoadingMore: boolean;
  isRefreshing: boolean;
  isLoadingMetricas: boolean;
  isSaving: boolean;
  isLoadingOuvidorias: boolean;

  filtros: SolicitacaoFiltros;
  error: string | null;

  fetchSolicitacoes: () => Promise<void>;
  fetchSolicitacoesParciais: () => Promise<void>;
  fetchStatsSolicitacoes: () => Promise<void>;
  fetchMoreSolicitacoes: () => Promise<void>;
  refreshSolicitacoes: () => Promise<void>;
  fetchSolicitacaoById: (id: string) => Promise<void>;
  fetchCategorias: () => Promise<void>;
  fetchMetricas: () => Promise<void>;
  fetchOuvidoriasSupervisionadas: (supervisoraId?: string) => Promise<void>;

  atualizarStatus: (
    id: string,
    novoStatus: StatusSolicitacao,
    observacao?: string,
  ) => Promise<void>;
  aprovarSolicitacao: (id: string, observacao?: string) => Promise<void>;
  reprovarSolicitacao: (id: string, justificativa: string) => Promise<void>;
  atribuirAgente: (
    solicitacaoId: string,
    agenteId: string,
    principal?: boolean,
  ) => Promise<void>;
  atribuirAgenteLote: (solicitacaoIds: string[], agenteId: string) => Promise<void>;
  arquivarSolicitacaoLote: (solicitacaoIds: string[], arquivar?: boolean) => Promise<void>;
  removerAgente: (solicitacaoId: string, agenteId: string) => Promise<void>;
  transferirSolicitacao: (
    solicitacaoId: string,
    ouvidoriaId: string,
  ) => Promise<void>;
  adicionarComentario: (
    solicitacaoId: string,
    conteudo: string,
    visivelCidadao?: boolean,
  ) => Promise<void>;
  adicionarObservacaoInterna: (id: string, observacao: string) => Promise<void>;

  setFiltros: (filtros: Partial<SolicitacaoFiltros>) => void;
  resetFiltros: () => void;
  clearError: () => void;
}

const defaultFiltros: SolicitacaoFiltros = {
  status: "all",
  tipo: "all",
  categoria_id: "all",
  prioridade: "all",
  resultado_validacao: "all",
  sla_status: "all",
  busca: "",
  estadoAprovacao: "all",
  // Supervisora vê próprias solicitações + das supervisionadas; ignorado se não puder supervisionar.
  incluirSupervisionadas: true,
};

function buildQueryParams(
  filtros: SolicitacaoFiltros,
  useAgenteEndpoint: boolean = false,
): Record<string, unknown> {
  const params: Record<string, unknown> = {};

  // Para endpoint de agente, sempre inclui agenteId e ouvidoriaId
  if (useAgenteEndpoint) {
    if (AppConfig.agenteId) params["agenteId"] = AppConfig.agenteId;
    if (AppConfig.ouvidoriaId) params["ouvidoriaId"] = AppConfig.ouvidoriaId;
  }

  // Paginação - PageNumber é 1-indexed na API
  if (filtros.pagina && filtros.pagina > 0) {
    params["PageNumber"] = filtros.pagina;
  }
  if (filtros.limite && filtros.limite > 0) {
    params["PageSize"] = filtros.limite;
  }

  if (filtros.status && filtros.status !== "all") {
    // Mapeia o status para o formato da API
    const statusApiMap: Record<string, string> = {
      pendente: "Pendente",
      EmAnalise: "EmAnalise",
      agendada: "Agendada",
      resolvida: "Resolvida",
      AtribuidasAMim: "AtribuidasAMim",
    };
    params["status"] = statusApiMap[filtros.status] || filtros.status;
  }
  if (filtros.tipo && filtros.tipo !== "all") params["tipo"] = filtros.tipo;
  if (filtros.busca) params["busca"] = filtros.busca;
  if (filtros.estadoAprovacao && filtros.estadoAprovacao !== "all") {
    // Envia direto sem mapeamento - API aceita: AguardandoAprovacao, Aprovada, Reprovada
    params["estadoAprovacao"] = filtros.estadoAprovacao;
  }

  // Hierarquia: só faz sentido no endpoint da ouvidoria, não no de agente
  if (!useAgenteEndpoint && filtros.incluirSupervisionadas === true) {
    params["incluirSupervisionadas"] = true;
  }

  return params;
}

function mapApiToParcial(item: any): SolicitacaoParcial {
  const upnValue = item.upn || "";
  const statusRaw = (item.status || "").toString().toLowerCase();
  const statusKey = statusRaw
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/\s+/g, " ")
    .trim();

  const statusMap: Record<string, StatusSolicitacao> = {
    pendente: "pendente",
    "aguardando aprovacao": "pendente",
    aguardando: "pendente",
    "em analise": "EmAnalise",
    emanalise: "EmAnalise",
    "em atendimento": "EmAnalise",
    agendada: "agendada",
    concluida: "resolvida",
    resolvida: "resolvida",
    aprovada: "aprovada",
    reprovada: "reprovada",
  };

  const status = statusMap[statusKey] || "pendente";

  const result = {
    id: item.id.toString() || "",
    topico: {
      id: item.topico?.id?.toString() || item.topicoId?.toString() || "",
      descricao: item.topico?.descricao || item.descricao || "",
      etiquetas: item.topico?.etiquetas || [],
      arquivado: false,
      ativo: true,
      metodoValidacao: "Agente" as const,
      status: "Aprovado" as const,
      tipo: "Problema" as const,
    },
    upn: upnValue,
    arquivada: item.arquivada || false,
    status,
    estadoAprovacao: item.estadoAprovacao || "aguardando",
    dataCriacao: item.dataCriacao || item.created_at || "",
    totalApoiadores: item.totalApoiadores || 0,
    endereco: item.endereco
      ? {
          logradouro: item.endereco.logradouro || "",
          numero: item.endereco.numero || "",
          bairro: item.endereco.bairro || "",
          cidade: item.endereco.cidade || "",
          estado: item.endereco.estado || "",
          cep: item.endereco.cep || "",
          pais: item.endereco.pais || "Brasil",
        }
      : undefined,
    anexos: (item.anexos || []).map((a: any) => ({
      id: a.id?.toString() || "",
      arquivo: a.arquivo || a.url || a.caminho || "",
    })),
  };

  return result;
}

function mapApiToSolicitacao(item: any): Solicitacao {
  const endereco = item.endereco
    ? `${item.endereco.logradouro || ""}, ${item.endereco.numero || ""} - ${item.endereco.bairro || ""}, ${item.endereco.cidade || ""}`.trim()
    : "";

  const statusRaw = (item.status || "").toString().toLowerCase();
  const statusKey = statusRaw
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/\s+/g, " ")
    .trim();

  const statusMap: Record<string, string> = {
    pendente: "Pendente",
    "aguardando aprovacao": "Pendente",
    aguardando: "Pendente",
    "em analise": "EmAnalise",
    emanalise: "EmAnalise",
    agendada: "Agendada",
    "em atendimento": "EmAnalise",
    concluida: "Resolvida",
    resolvida: "Resolvida",
    aprovada: "Aprovada",
    reprovada: "Reprovada",
    arquivada: "Arquivada",
  };

  const statusTokenMap: Record<string, StatusSolicitacao> = {
    Pendente: "pendente",
    EmAnalise: "EmAnalise",
    Agendada: "agendada",
    Resolvida: "resolvida",
    Aprovada: "aprovada",
    Reprovada: "reprovada",
  };

  const status =
    statusTokenMap[statusMap[statusKey] || "Pendente"] || "pendente";

  return {
    id: item.id?.toString() || "",
    titulo: item.topico?.descricao || item.titulo || "Sem título",
    descricao: item.topico?.descricao || item.descricao || "Sem título",
    protocolo: item.upn || item.protocolo || "N/A",
    tipo: "reclamacao",
    status,
    prioridade: "media",
    categoria_id: item.topico?.id?.toString() || "",
    categoria_texto: item.topico?.descricao || "",
    categoria: item.topico,
    agenteResponsavel: item.agenteResponsavel,
    municipio_id: item.endereco?.ibge || "",
    cidadao_anonimo: item.cidadaoAnonimo || false,
    localizacao_endereco: endereco || null,
    localizacao_bairro: item.endereco?.bairro || null,
    localizacao_lat: item.latitude ?? null,
    localizacao_lng: item.longitude ?? null,
    sla_horas: 48,
    sla_vencimento: null,
    apoios: item.totalApoiadores || 0,
    visualizacoes: 0,
    resultado_validacao: ((item.estadoAprovacao == null
      ? "aguardando"
      : item.estadoAprovacao.toString()
    ).toLowerCase() || "aguardando") as ResultadoValidacao,
    tipo_validacao: ((item.topico?.metodoValidacao == null
      ? "manual"
      : item.topico.metodoValidacao.toString()
    ).toLowerCase() || "manual") as TipoValidacao,
    grau_confianca_validacao: null,
    justificativa_reprovacao: null,
    observacoes_internas: null,
    data_primeira_resposta: null,
    data_conclusao: item.dataConclusao || null,
    data_ocorrencia: null,
    created_at: item.dataCriacao || item.created_at || "",
    updated_at: item.dataAtualizacao || item.updated_at || "",
    anexos: (item.anexos || []).map((a: any) => ({
      id: a.id?.toString() || "",
      solicitacao_id: item.id?.toString() || "",
      tipo: "imagem" as const,
      url: a.arquivo || a.url || "",
      nome_arquivo: a.nomeArquivo || a.nome_arquivo || null,
      tamanho_bytes: a.tamanho || a.tamanho_bytes || null,
      created_at: a.dataCriacao || a.created_at || "",
    })),
    historico: [],
    agentes: [],
    comentarios: [],
  };
}

export const useSolicitacoesStore = create<SolicitacoesState>((set, get) => ({
  solicitacoes: [],
  solicitacoesParciais: [],
  statsSolicitacoes: [],
  solicitacaoAtual: null,
  categorias: [],
  metricas: null,
  totalSolicitacoes: 0,
  ouvidoriasSupervisionadas: [],
  isLoading: false,
  isLoadingMore: false,
  isRefreshing: false,
  isLoadingMetricas: false,
  isSaving: false,
  isLoadingOuvidorias: false,
  filtros: { ...defaultFiltros },
  error: null,

  fetchSolicitacoesParciais: async () => {
    const { filtros } = get();
    set({ isLoading: true, error: null });

    try {
      const baseParams = buildQueryParams(filtros, false); // usa endpoint de ouvidoria
      const pageSize = 500;
      const allItems: unknown[] = [];
      let page = 1;

      // Busca todas as páginas com os filtros atuais para permitir paginação
      // client-side correta (total de páginas, page size, etc.).
      while (true) {
        const result = await api.get<unknown[]>({
          endpoint: getSolicitacoesEndpoint(),
          headers: { accept: "application/vnd.data.partial.v1+json" },
          queryParams: { ...baseParams, PageNumber: page, PageSize: pageSize },
          forceRefresh: true,
          serialize: (data) => {
            if (Array.isArray(data)) return data;
            if (data && typeof data === "object" && "items" in (data as any))
              return (data as any).items;
            if (data && typeof data === "object" && "data" in (data as any))
              return (data as any).data;
            return [];
          },
        });

        if ("failure" in result) {
          throw new Error(result.failure.message);
        }

        const items = result.success.data || [];
        if (items.length === 0) break;

        allItems.push(...items);

        const rawBody = result.success.body as Record<string, unknown> | undefined;
        const totalCount =
          typeof rawBody?.totalCount === "number"
            ? rawBody.totalCount
            : typeof rawBody?.total === "number"
              ? rawBody.total
              : typeof rawBody?.totalRecords === "number"
                ? rawBody.totalRecords
                : typeof rawBody?.count === "number"
                  ? rawBody.count
                  : undefined;

        if (typeof totalCount === "number" && allItems.length >= totalCount) break;
        if (items.length < pageSize) break;

        page++;
      }

      const parciais = allItems.map(mapApiToParcial);

      set({
        solicitacoesParciais: parciais,
        totalSolicitacoes: parciais.length,
        isLoading: false,
      });
    } catch (error: any) {
      set({
        isLoading: false,
        error: error.message || "Erro ao carregar solicitações",
      });
    }
  },

  // Busca TODAS as solicitações sem aplicar filtros. Usado pelos cards de contagem
  // para que os números não mudem quando o usuário filtra a lista.
  // Itera por todas as páginas disponíveis para garantir contagens completas.
  fetchStatsSolicitacoes: async () => {
    try {
      const baseParams = buildQueryParams({ ...defaultFiltros }, false); // usa endpoint de ouvidoria
      const pageSize = 500;
      const allItems: unknown[] = [];
      let page = 1;

      while (true) {
        const result = await api.get<unknown[]>({
          endpoint: getSolicitacoesEndpoint(),
          headers: { accept: "application/vnd.data.partial.v1+json" },
          queryParams: { ...baseParams, PageNumber: page, PageSize: pageSize },
          forceRefresh: true,
          serialize: (data) => {
            if (Array.isArray(data)) return data;
            if (data && typeof data === "object" && "items" in (data as any))
              return (data as any).items;
            if (data && typeof data === "object" && "data" in (data as any))
              return (data as any).data;
            return [];
          },
        });

        if ("failure" in result) {
          throw new Error(result.failure.message);
        }

        const items = result.success.data || [];
        if (items.length === 0) break;

        allItems.push(...items);

        const rawBody = result.success.body as Record<string, unknown> | undefined;
        const totalCount =
          typeof rawBody?.totalCount === "number"
            ? rawBody.totalCount
            : typeof rawBody?.total === "number"
              ? rawBody.total
              : typeof rawBody?.totalRecords === "number"
                ? rawBody.totalRecords
                : typeof rawBody?.count === "number"
                  ? rawBody.count
                  : undefined;

        if (typeof totalCount === "number" && allItems.length >= totalCount) break;
        if (items.length < pageSize) break;

        page++;
      }

      set({ statsSolicitacoes: allItems.map(mapApiToParcial) });
    } catch (error: any) {
      console.error("Erro ao buscar stats de solicitações:", error);
    }
  },

  fetchSolicitacoes: async () => {
    const { filtros } = get();
    set({ isLoading: true, error: null });

    try {
      const result = await api.get<unknown[]>({
        endpoint: getSolicitacoesEndpoint(),
        headers: { accept: "application/vnd.data.partial.v1+json" },
        queryParams: buildQueryParams(filtros, false), // usa endpoint de ouvidoria
        forceRefresh: true,
        serialize: (data) => {
          if (Array.isArray(data)) return data;
          if (data && typeof data === "object" && "items" in (data as any))
            return (data as any).items;
          if (data && typeof data === "object" && "data" in (data as any))
            return (data as any).data;
          return [];
        },
      });

      if ("failure" in result) {
        throw new Error(result.failure.message);
      }

      const items = result.success.data || [];
      set({
        solicitacoes: items.map((item: any) => mapApiToSolicitacao(item)),
        isLoading: false,
      });
    } catch (error: any) {
      set({
        isLoading: false,
        error: error.message || "Erro ao carregar solicitações",
      });
    }
  },

  fetchMoreSolicitacoes: async () => {
    const { filtros, solicitacoesParciais, totalSolicitacoes } = get();
    if (
      solicitacoesParciais.length >= totalSolicitacoes &&
      totalSolicitacoes > 0
    )
      return;

    set({ isLoadingMore: true, error: null });

    try {
      const nextPage = filtros.pagina + 1;
      const nextFiltros = { ...filtros, pagina: nextPage };

      const result = await api.get<unknown[]>({
        endpoint: getSolicitacoesEndpoint(),
        headers: { accept: "application/vnd.data.partial.v1+json" },
        queryParams: buildQueryParams(nextFiltros, false), // usa endpoint de ouvidoria
        forceRefresh: true,
        serialize: (data) => {
          if (Array.isArray(data)) return data;
          if (data && typeof data === "object" && "items" in (data as any))
            return (data as any).items;
          return [];
        },
      });

      if ("failure" in result) {
        throw new Error(result.failure.message);
      }

      const newItems = (result.success.data || []).map(mapApiToParcial);

      set({
        solicitacoesParciais: [...solicitacoesParciais, ...newItems],
        filtros: nextFiltros,
        isLoadingMore: false,
      });
    } catch (error: any) {
      set({
        isLoadingMore: false,
        error: error.message || "Erro ao carregar mais solicitações",
      });
    }
  },

  refreshSolicitacoes: async () => {
    set({ isRefreshing: true, filtros: { ...get().filtros, pagina: 1 } });
    try {
      await Promise.all([
        get().fetchSolicitacoesParciais(),
        get().fetchSolicitacoes(),
        get().fetchStatsSolicitacoes(),
      ]);
    } finally {
      set({ isRefreshing: false });
    }
  },

  fetchSolicitacaoById: async (id: string) => {
    set({ isLoading: true, error: null, solicitacaoAtual: null });

    try {
      const result = await api.get<Record<string, unknown>>({
        endpoint: `${SOLICITACOES_ENDPOINT_DETAILS}/${id}`,
        headers: { Accept: "application/vnd.data.full.v1+json" },
        forceRefresh: true,
        serialize: (data) => data as Record<string, unknown>,
      });

      if ("failure" in result) {
        throw new Error(result.failure.message);
      }

      let rawData = result.success.data || result.success.body;

      // Algumas respostas podem vir como array com um único item
      if (Array.isArray(rawData)) {
        rawData = rawData[0];
      }

      if (
        !rawData ||
        (typeof rawData === "object" && Object.keys(rawData).length === 0)
      ) {
        throw new Error("Nenhum dado retornado pela API");
      }

      const solicitacao = mapApiToSolicitacao(rawData);
      set({ solicitacaoAtual: solicitacao, isLoading: false });
    } catch (error: any) {
      set({
        isLoading: false,
        error: error.message || "Erro ao carregar detalhes da solicitação",
      });
    }
  },

  fetchCategorias: async () => {
    // Categories come from the API topics endpoint
    try {
      const result = await api.get<unknown[]>({
        endpoint: "v1/topicos",
        headers: { accept: "application/vnd.data.compact.v1+json" },
        forceRefresh: true,
        serialize: (data) => {
          if (Array.isArray(data)) return data;
          return [];
        },
      });

      if ("failure" in result) return;

      const categorias: CategoriaSolicitacao[] = (
        result.success.data || []
      ).map((t: any) => ({
        id: t.id?.toString() || "",
        nome: t.descricao || "",
        descricao: t.contexto || null,
        sla_horas: 48,
        ativo: true,
        created_at: "",
      }));

      set({ categorias });
    } catch (error: any) {
    }
  },

  fetchMetricas: async () => {
    set({ isLoadingMetricas: true });
    // Metrics computed from whatever solicitações we have
    try {
      await get().fetchSolicitacoes();
      const { solicitacoes } = get();

      const agora = new Date();
      const metricas: DashboardMetricas = {
        total_abertas: 0,
        total_em_analise: 0,
        total_concluidas: 0,
        total_arquivadas: 0,
        total_reprovadas: 0,
        aguardando_aprovacao: 0,
        sla_dentro_prazo: 0,
        sla_alerta: 0,
        sla_estourado: 0,
        sla_medio_percentual: 0,
        tempo_medio_resposta_horas: 0,
        tempo_medio_resolucao_horas: 0,
        comparacao_mes_anterior: { total: 0, percentual: 0 },
      };

      solicitacoes.forEach((s: any) => {
        switch (s.status) {
          case "aguardando_aprovacao":
            metricas.aguardando_aprovacao++;
            break;
          // case "Aberta":
          //   metricas.aguardando_aprovacao++;
          //   metricas.total_abertas++;
          //   break;
          case "aprovada":
          case "Aprovada":
            metricas.total_abertas++;
            break;
          case "EmAnalise":
            metricas.total_em_analise++;
            break;
          case "Resolvida":
            metricas.total_concluidas++;
            break;
          case "arquivada":
          case "Arquivada":
            metricas.total_arquivadas++;
            break;
          case "reprovada":
          case "Reprovada":
            metricas.total_reprovadas++;
            break;
        }

        if (
          s.sla_vencimento &&
          s.status !== "concluida" &&
          s.status !== "arquivada"
        ) {
          const horasRestantes =
            (new Date(s.sla_vencimento).getTime() - agora.getTime()) /
            (1000 * 60 * 60);
          if (horasRestantes < 0) metricas.sla_estourado++;
          else if (horasRestantes <= 12) metricas.sla_alerta++;
          else metricas.sla_dentro_prazo++;
        }
      });

      const totalSLA =
        metricas.sla_dentro_prazo +
        metricas.sla_alerta +
        metricas.sla_estourado;
      if (totalSLA > 0)
        metricas.sla_medio_percentual =
          (metricas.sla_dentro_prazo / totalSLA) * 100;

      set({ metricas, isLoadingMetricas: false });
    } catch (error: any) {
      set({ isLoadingMetricas: false });
    }
  },

  atualizarStatus: async (
    id: string,
    novoStatus: StatusSolicitacao,
    observacao?: string,
  ) => {
    const { solicitacaoAtual } = get();
    // Otimista: atualiza status localmente para reatividade imediata
    if (solicitacaoAtual && solicitacaoAtual.id === id) {
      set({ solicitacaoAtual: { ...solicitacaoAtual, status: novoStatus } });
    }

    set({ isSaving: true, error: null });
    try {
      const result = await api.post<Record<string, unknown>>({
        endpoint: `${SOLICITACOES_ENDPOINT_DETAILS}/${id}/alterar-status`,
        queryParams: { status: novoStatus },
        body: observacao ? { observacao } : undefined,
      });

      if ("failure" in result) throw new Error(result.failure.message);

      // Recarrega detalhes e listas para reatividade em todas as telas
      await get().fetchSolicitacaoById(id);
      await get().fetchSolicitacoesParciais();
      await get().fetchSolicitacoes();
      set({ isSaving: false });
    } catch (error: any) {
      set({
        isSaving: false,
        error: error.message || "Erro ao atualizar status",
      });
    }
  },

  aprovarSolicitacao: async (id: string, observacao?: string) => {
    set({ isSaving: true, error: null });
    try {
      const result = await api.post<Record<string, unknown>>({
        endpoint: `${SOLICITACOES_ENDPOINT_DETAILS}/${id}/alterar-estado-aprovacao`,
        queryParams: { estadoAprovacao: "Aprovada" },
      });

      if ("failure" in result) throw new Error(result.failure.message);

      await Promise.all([
        get().fetchSolicitacaoById(id),
        get().fetchSolicitacoesParciais(),
        get().fetchSolicitacoes(),
      ]);
      set({ isSaving: false });
    } catch (error: any) {
      set({
        isSaving: false,
        error: error.message || "Erro ao aprovar solicitação",
      });
    }
  },

  reprovarSolicitacao: async (id: string, justificativa: string) => {
    set({ isSaving: true, error: null });
    try {
      const result = await api.post<Record<string, unknown>>({
        endpoint: `${SOLICITACOES_ENDPOINT_DETAILS}/${id}/alterar-estado-aprovacao`,
        queryParams: { estadoAprovacao: "Reprovada" },
      });

      if ("failure" in result) throw new Error(result.failure.message);

      await Promise.all([
        get().fetchSolicitacaoById(id),
        get().fetchSolicitacoesParciais(),
        get().fetchSolicitacoes(),
      ]);
      set({ isSaving: false });
    } catch (error: any) {
      set({
        isSaving: false,
        error: error.message || "Erro ao reprovar solicitação",
      });
    }
  },

  atribuirAgente: async (
    solicitacaoId: string,
    agenteId: string,
    principal = false,
  ) => {
    set({ isSaving: true, error: null });
    try {
      const result = await api.post<Record<string, unknown>>({
        endpoint: `${SOLICITACOES_ENDPOINT_DETAILS}/${solicitacaoId}/agente/${agenteId}/atribuir-agente`,
      });

      if ("failure" in result) throw new Error(result.failure.message);

      await Promise.all([
        get().fetchSolicitacaoById(solicitacaoId),
        get().fetchSolicitacoesParciais(),
        get().fetchSolicitacoes(),
      ]);
      set({ isSaving: false });
    } catch (error: any) {
      set({
        isSaving: false,
        error: error.message || "Erro ao atribuir agente",
      });
    }
  },

  atribuirAgenteLote: async (solicitacaoIds: string[], agenteId: string) => {
    set({ isSaving: true, error: null });
    try {
      await Promise.all(
        solicitacaoIds.map((id) =>
          api.post<Record<string, unknown>>({
            endpoint: `${SOLICITACOES_ENDPOINT_DETAILS}/${id}/agente/${agenteId}/atribuir-agente`,
          })
        )
      );

      await Promise.all([
        get().fetchSolicitacoesParciais(),
        get().fetchSolicitacoes(),
      ]);
      set({ isSaving: false });
    } catch (error: any) {
      set({
        isSaving: false,
        error: error.message || "Erro ao atribuir agente",
      });
      throw error;
    }
  },

  arquivarSolicitacaoLote: async (solicitacaoIds: string[], arquivar = true) => {
    set({ isSaving: true, error: null });
    try {
      await Promise.all(
        solicitacaoIds.map((id) => {
          const endpoint = arquivar
            ? OUVIDORIA_ENDPOINTS.arquivarSolicitacao(id)
            : OUVIDORIA_ENDPOINTS.restaurarSolicitacao(id);
          return api.post<Record<string, unknown>>({
            endpoint,
          });
        })
      );

      await Promise.all([
        get().fetchSolicitacoesParciais(),
        get().fetchSolicitacoes(),
      ]);
      set({ isSaving: false });
    } catch (error: any) {
      set({
        isSaving: false,
        error: error.message || "Erro ao arquivar solicitações",
      });
      throw error;
    }
  },

  removerAgente: async (solicitacaoId: string, agenteId: string) => {
    set({ isSaving: true, error: null });
    try {
      const result = await api.delete({
        endpoint: `${getSolicitacoesEndpoint()}/${solicitacaoId}/agentes/${agenteId}`,
      });

      if ("failure" in result) throw new Error(result.failure.message);

      await get().fetchSolicitacaoById(solicitacaoId);
      set({ isSaving: false });
    } catch (error: any) {
      set({
        isSaving: false,
        error: error.message || "Erro ao remover agente",
      });
    }
  },

  adicionarComentario: async (
    solicitacaoId: string,
    conteudo: string,
    visivelCidadao = false,
  ) => {
    set({ isSaving: true, error: null });
    try {
      const result = await api.post<Record<string, unknown>>({
        endpoint: `${getSolicitacoesEndpoint()}/${solicitacaoId}/comentarios`,
        body: { conteudo, visivelCidadao },
      });

      if ("failure" in result) throw new Error(result.failure.message);

      await get().fetchSolicitacaoById(solicitacaoId);
      set({ isSaving: false });
    } catch (error: any) {
      set({
        isSaving: false,
        error: error.message || "Erro ao adicionar comentário",
      });
    }
  },

  adicionarObservacaoInterna: async (id: string, observacao: string) => {
    set({ isSaving: true, error: null });
    try {
      const result = await api.put<Record<string, unknown>>({
        endpoint: `${getSolicitacoesEndpoint()}/${id}`,
        body: { observacoesInternas: observacao },
      });

      if ("failure" in result) throw new Error(result.failure.message);

      await get().fetchSolicitacaoById(id);
      set({ isSaving: false });
    } catch (error: any) {
      set({
        isSaving: false,
        error: error.message || "Erro ao adicionar observação",
      });
    }
  },

  setFiltros: (novosFiltros: Partial<SolicitacaoFiltros>) => {
    const { filtros } = get();
    // Se o usuário explicitamente enviar pagina, respeita; caso contrário, reseta para 1.
    const novaPagina = novosFiltros.pagina !== undefined ? novosFiltros.pagina : 1;
    set({ filtros: { ...filtros, ...novosFiltros, pagina: novaPagina } });
  },

  resetFiltros: () => {
    set({ filtros: { ...defaultFiltros } });
  },

  clearError: () => {
    set({ error: null });
  },

  fetchOuvidoriasSupervisionadas: async (supervisoraId) => {
    // A rota é indexada pelo ID da supervisora — usa o explícito ou a ouvidoria
    // atual quando ela mesma é a supervisora (Geral ou Associação Supervisora).
    const id = supervisoraId || AppConfig.ouvidoriaId;
    if (!id || !AppConfig.municipioId) {
      set({ isLoadingOuvidorias: false, ouvidoriasSupervisionadas: [] });
      return;
    }

    set({ isLoadingOuvidorias: true });
    try {
      const result = await api.get<Ouvidoria[]>({
        endpoint: `v1/municipios/${AppConfig.municipioId}/ouvidorias/${id}/supervisionadas`,
        headers: { accept: "application/vnd.data.compact.v1+json" },
        forceRefresh: true,
        serialize: (data) => {
          if (Array.isArray(data)) return data;
          if (data && typeof data === "object" && "items" in (data as any))
            return (data as any).items;
          if (data && typeof data === "object" && "data" in (data as any))
            return (data as any).data;
          return [];
        },
      });

      if ("failure" in result) {
        throw new Error(result.failure.message);
      }

      set({ 
        ouvidoriasSupervisionadas: result.success.data || [],
        isLoadingOuvidorias: false 
      });
    } catch (error: any) {
      set({
        isLoadingOuvidorias: false,
        error: error.message || "Erro ao carregar ouvidorias",
      });
    }
  },

  transferirSolicitacao: async (
    solicitacaoId: string,
    ouvidoriaId: string,
  ) => {
    set({ isSaving: true, error: null });
    try {
      const result = await api.post<Record<string, unknown>>({
        endpoint: `${SOLICITACOES_ENDPOINT_DETAILS}/${solicitacaoId}/ouvidoria/${ouvidoriaId}/transferir-solicitacao`,
      });

      if ("failure" in result) throw new Error(result.failure.message);

      await Promise.all([
        get().fetchSolicitacaoById(solicitacaoId),
        get().fetchSolicitacoesParciais(),
        get().fetchSolicitacoes(),
      ]);
      set({ isSaving: false });
    } catch (error: any) {
      set({
        isSaving: false,
        error: error.message || "Erro ao transferir solicitação",
      });
    }
  },
}));
